/**
 * Admin API Service
 * Client for admin endpoints
 */

import { apiClient } from './api.client';
import { asArray, extractListItems, unwrapNamedList } from './api.unwrap';

export interface User {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  status: string;
  createdAt: string;
}

export interface SystemOverview {
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  deletedUsers: number;
  totalBrands: number;
  totalDeals: number;
  totalDealValue: number;
  totalStorageBytes: number;
  totalFiles: number;
}

export interface UserStats {
  userId: string;
  email: string;
  displayName: string;
  status: string;
  roles: string[];
  skuId?: string;
  subscriptionTier?: string;
  createdAt: string;
  lastLogin?: string;
  usage: {
    totalBrands: number;
    totalDeals: number;
    totalDealValue: number;
    storage: {
      totalBytes: number;
      fileCount: number;
      documents: { count: number; bytes: number };
      images: { count: number; bytes: number };
      videos: { count: number; bytes: number };
      lastUploadAt?: string;
    };
  };
}

export interface UserUsageStats {
  userId: string;
  email: string;
  displayName: string;
  totalStorageBytes: number;
  fileCount: number;
  lastUploadAt?: string;
}

export interface AuditLog {
  id: string;
  userId: string;
  action: string;
  resourceType: string;
  resourceId: string;
  timestamp: string;
  changes?: any;
}

export const adminService = {
  /**
   * List all users
   */
  async listUsers(params?: {
    status?: string;
    role?: string;
    searchTerm?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.role) queryParams.append('role', params.role);
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const response = await apiClient.get(`/api/management/users?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Get user details
   */
  async getUserDetails(userId: string) {
    const response = await apiClient.get(`/api/management/users/${userId}`);
    return response.data;
  },

  /**
   * Get user stats (detailed usage info)
   */
  async getUserStats(userId: string): Promise<UserStats> {
    const response = await apiClient.get(`/api/management/users/${userId}/stats`);
    return response.data.data;
  },

  /**
   * Update user status
   */
  async updateUserStatus(userId: string, status: string) {
    const response = await apiClient.patch(`/api/management/users/${userId}/status`, { status });
    return response.data;
  },

  /**
   * Update user roles (super-admin only)
   */
  async updateUserRoles(userId: string, roles: string[]) {
    const response = await apiClient.patch(`/api/management/users/${userId}/roles`, { roles });
    return response.data;
  },

  /**
   * Soft delete user
   */
  async deleteUser(userId: string) {
    const response = await apiClient.delete(`/api/management/users/${userId}`);
    return response.data;
  },

  /**
   * Hard delete user (super-admin only)
   */
  async hardDeleteUser(userId: string) {
    const response = await apiClient.delete(`/api/management/users/${userId}/hard-delete`);
    return response.data;
  },

  /**
   * Update user SKU (super-admin only)
   */
  async updateUserSku(userId: string, skuId: string, reason?: string) {
    const response = await apiClient.patch(`/api/management/users/${userId}/sku`, {
      skuId,
      reason,
    });
    return response.data;
  },

  /**
   * Get system overview
   */
  async getSystemOverview(): Promise<{ data: SystemOverview }> {
    const response = await apiClient.get('/api/management/analytics/overview');
    return response.data;
  },

  /**
   * Get user usage stats (for analytics page)
   */
  async getUserUsageStats(params?: {
    sortBy?: 'totalStorageBytes' | 'fileCount';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    continuationToken?: string;
  }): Promise<{ items: UserUsageStats[]; continuationToken?: string }> {
    const queryParams = new URLSearchParams();
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.continuationToken)
      queryParams.append('continuationToken', params.continuationToken);

    const response = await apiClient.get(
      `/api/management/analytics/users?${queryParams.toString()}`
    );
    const envelope = response.data;
    const items = unwrapNamedList<UserUsageStats>(envelope, 'users');
    // Azure may return a flat array in data with pagination at root
    const fallbackItems =
      items.length > 0 ? items : asArray<UserUsageStats>(extractListItems(envelope?.data));

    return {
      items: fallbackItems,
      continuationToken: envelope?.pagination?.continuationToken ?? envelope?.continuationToken,
    };
  },

  /**
   * Get storage breakdown
   */
  async getStorageBreakdown(): Promise<any> {
    const response = await apiClient.get('/api/management/analytics/storage');
    return response.data.data;
  },

  /**
   * Get audit logs
   */
  async getAuditLogs(params?: {
    action?: string;
    resourceType?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    continuationToken?: string;
  }): Promise<{ items: AuditLog[]; continuationToken?: string }> {
    const queryParams = new URLSearchParams();
    if (params?.action) queryParams.append('action', params.action);
    if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
    if (params?.userId) queryParams.append('userId', params.userId);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.continuationToken)
      queryParams.append('continuationToken', params.continuationToken);

    const response = await apiClient.get(`/api/management/audit-logs?${queryParams.toString()}`);
    const envelope = response.data;
    // Azure: data is AuditLog[]; CF: data is { data: AuditLog[], pagination }
    const items = asArray<AuditLog>(extractListItems(envelope?.data));
    return {
      items,
      continuationToken:
        envelope?.pagination?.continuationToken ??
        (typeof envelope?.data === 'object' &&
        envelope?.data !== null &&
        !Array.isArray(envelope.data)
          ? envelope.data.pagination?.continuationToken
          : undefined) ??
        envelope?.continuationToken,
    };
  },

  /**
   * Start impersonation session
   * @param userId - Target user ID to impersonate
   * @param reason - Required justification for impersonation
   * @param timeoutMinutes - Optional session timeout in minutes
   */
  async startImpersonation(
    userId: string,
    reason: string,
    timeoutMinutes?: number
  ): Promise<{
    success: boolean;
    session: {
      sessionId: string;
      targetUser: { userId: string; email: string; displayName: string };
      expiresAt: string;
      timeoutMinutes: number;
    };
  }> {
    const response = await apiClient.post(`/api/management/impersonate/${userId}`, {
      reason,
      timeoutMinutes,
    });
    return response.data;
  },

  /**
   * End current impersonation session
   */
  async endImpersonation(): Promise<{
    success: boolean;
    message: string;
    durationMinutes?: number;
  }> {
    const response = await apiClient.post('/api/impersonation/end');
    return response.data;
  },

  /**
   * Get current impersonation status
   */
  async getImpersonationStatus(): Promise<{
    isImpersonating: boolean;
    session?: {
      sessionId: string;
      targetUserId: string;
      targetEmail: string;
      targetDisplayName?: string;
      startedAt: string;
      expiresAt: string;
      reason: string;
    };
  }> {
    const response = await apiClient.get('/api/impersonation/status');
    return response.data.data;
  },
};
